import { EMPLOYMENT_TYPES } from '@hygge/shared';
import type { FilterConfig, FilterOption } from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * Filter state for employee assignment selection.
 * Uses arrays for multi-select filters to allow selecting multiple values.
 */
export interface AssignmentFilters {
  /** Text search by name or email */
  search: string;
  /** Multiple departments */
  departmentIds: string[];
  /** Multiple positions */
  positionIds: string[];
  /** Multiple seniority levels */
  seniorityLevelIds: string[];
  /** Multiple divisions */
  divisionIds: string[];
  /** Multiple locations */
  locationIds: string[];
  /** Multiple employment types */
  employmentTypes: string[];
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default values for assignment filters.
 * Empty arrays mean "all" (no filtering).
 */
export const DEFAULT_ASSIGNMENT_FILTERS: AssignmentFilters = {
  search: '',
  departmentIds: [],
  positionIds: [],
  seniorityLevelIds: [],
  divisionIds: [],
  locationIds: [],
  employmentTypes: [],
};

/**
 * Employment type options for multi-select.
 */
const EMPLOYMENT_TYPE_OPTIONS: FilterOption[] = EMPLOYMENT_TYPES.map((t) => ({
  value: t.value,
  label: t.label,
}));

// ============================================================================
// Configuration
// ============================================================================

/**
 * Filter configuration for employee assignment selection.
 * Uses multi-select for organizational criteria to allow flexible filtering.
 *
 * @example
 * ```tsx
 * // Select all Sales and Account employees
 * const filters = {
 *   ...DEFAULT_ASSIGNMENT_FILTERS,
 *   departmentIds: ['sales-id', 'account-id'],
 * };
 * ```
 */
export const assignmentFilterConfig: FilterConfig<AssignmentFilters> = {
  id: 'assignment-filters',
  title: 'Filter Employees',
  defaultValues: DEFAULT_ASSIGNMENT_FILTERS,

  fields: [
    // Search is handled separately in the UI, not as a filter field
    {
      key: 'departmentIds',
      type: 'multi-select',
      label: 'Departments',
      placeholder: 'All departments',
      defaultValue: [],
      options: 'departments',
    },
    {
      key: 'positionIds',
      type: 'multi-select',
      label: 'Positions',
      placeholder: 'All positions',
      defaultValue: [],
      options: 'positions',
    },
    {
      key: 'seniorityLevelIds',
      type: 'multi-select',
      label: 'Seniority Levels',
      placeholder: 'All levels',
      defaultValue: [],
      options: 'seniorityLevels',
    },
    {
      key: 'divisionIds',
      type: 'multi-select',
      label: 'Divisions',
      placeholder: 'All divisions',
      defaultValue: [],
      options: 'divisions',
    },
    {
      key: 'locationIds',
      type: 'multi-select',
      label: 'Locations',
      placeholder: 'All locations',
      defaultValue: [],
      options: 'locations',
    },
    {
      key: 'employmentTypes',
      type: 'multi-select',
      label: 'Employment Types',
      placeholder: 'All types',
      defaultValue: [],
      options: EMPLOYMENT_TYPE_OPTIONS,
    },
  ],

  /**
   * Transform filter state to API query parameters.
   * Empty arrays are converted to undefined (no filter).
   */
  toQueryParams: (filters) => {
    const params: Record<string, unknown> = {};

    // Text search
    if (filters.search) {
      params.search = filters.search;
    }

    // Multi-select filters - only include if has values
    if (filters.departmentIds.length > 0) {
      params.departmentIds = filters.departmentIds;
    }

    if (filters.positionIds.length > 0) {
      params.positionIds = filters.positionIds;
    }

    if (filters.seniorityLevelIds.length > 0) {
      params.seniorityLevelIds = filters.seniorityLevelIds;
    }

    if (filters.divisionIds.length > 0) {
      params.divisionIds = filters.divisionIds;
    }

    if (filters.locationIds.length > 0) {
      params.locationIds = filters.locationIds;
    }

    if (filters.employmentTypes.length > 0) {
      params.employmentTypes = filters.employmentTypes;
    }

    return params;
  },
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if any filters are active (non-default values).
 */
export function hasActiveAssignmentFilters(filters: AssignmentFilters): boolean {
  return (
    filters.search !== '' ||
    filters.departmentIds.length > 0 ||
    filters.positionIds.length > 0 ||
    filters.seniorityLevelIds.length > 0 ||
    filters.divisionIds.length > 0 ||
    filters.locationIds.length > 0 ||
    filters.employmentTypes.length > 0
  );
}

/**
 * Count the number of active filter criteria.
 */
export function countActiveAssignmentFilters(filters: AssignmentFilters): number {
  let count = 0;
  if (filters.search) count++;
  if (filters.departmentIds.length > 0) count++;
  if (filters.positionIds.length > 0) count++;
  if (filters.seniorityLevelIds.length > 0) count++;
  if (filters.divisionIds.length > 0) count++;
  if (filters.locationIds.length > 0) count++;
  if (filters.employmentTypes.length > 0) count++;
  return count;
}
